import { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { hrms } from '../../services/api';
import { theme } from '../../theme';

const PERIODS = [
  { key: 'daily',   label: 'Today' },
  { key: 'weekly',  label: 'This Week' },
  { key: 'monthly', label: 'This Month' },
  { key: 'yearly',  label: 'This Year' },
  { key: 'custom',  label: 'Custom' },
];

const PALETTE = [
  '#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6',
  '#14B8A6','#F97316','#6366F1','#EC4899','#6B7280',
];

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const avatarColor = (name = '') => ACCENTS[(name.charCodeAt(0) || 0) % ACCENTS.length];
const initials    = (name = '') => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
const fmt         = n => Number(n).toFixed(1);

export default function WorkHours({ project }) {
  const [period,     setPeriod]     = useState('weekly');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [search,     setSearch]     = useState('');
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [employees,  setEmployees]  = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { period, project_id: project.id };
      if (period === 'custom' && dateFrom) params.date_from = dateFrom;
      if (period === 'custom' && dateTo)   params.date_to   = dateTo;
      if (employeeId) params.employee_id = employeeId;
      const res = await hrms.getPMTimesheet(params);
      setData(res.data);
      if (res.data.employees?.length) {
        setEmployees(prev => {
          const map = {};
          [...prev, ...res.data.employees].forEach(e => { map[e.id] = e; });
          return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load work hours.');
    } finally { setLoading(false); }
  }, [period, dateFrom, dateTo, employeeId, project.id]);

  useEffect(() => { if (period !== 'custom') load(); }, [period, employeeId, project.id]); // eslint-disable-line

  const entries = (data?.entries || []).filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (e.employee_name || '').toLowerCase().includes(q) ||
      (e.emp_code      || '').toLowerCase().includes(q) ||
      (e.description   || '').toLowerCase().includes(q)
    );
  });

  const filteredHours = entries.reduce((s, r) => s + (r.hours || 0), 0);

  const pillBtn = (active) => ({
    padding: '0.3rem 0.8rem', borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s', border: 'none',
    background: active ? theme.primary : '#F1F5F9',
    color:      active ? '#fff'         : '#6B7280',
  });

  const inp = {
    padding: '0.4rem 0.75rem', border: '1.5px solid #E5E7EB',
    borderRadius: '0.5rem', fontSize: '0.82rem', color: '#111827',
    background: '#fff', outline: 'none',
  };

  return (
    <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'Inter',-apple-system,sans-serif" }}>
      <style>{`@keyframes wh-spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#111827' }}>Work Hours</h2>
          <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: '#6B7280' }}>
            {data
              ? `${data.date_from} → ${data.date_to} · ${data.total_entries} entries · ${fmt(data.total_hours)}h total`
              : `Hours logged by members of ${project.name}`}
          </p>
        </div>
        <button onClick={load} style={{ ...pillBtn(false), padding: '0.4rem 1rem' }}>↻ Refresh</button>
      </div>

      {/* ── Filters ── */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0.75rem', padding: '0.875rem 1.1rem', display: 'flex', flexWrap: 'wrap', gap: '0.625rem', alignItems: 'center' }}>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => { setPeriod(p.key); if (p.key !== 'custom') { setDateFrom(''); setDateTo(''); } }}
            style={pillBtn(period === p.key)}>
            {p.label}
          </button>
        ))}

        {period === 'custom' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inp, width: 130 }} />
            <span style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>→</span>
            <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={{ ...inp, width: 130 }} />
            {dateFrom && dateTo && (
              <button onClick={load} style={{ padding: '0.4rem 0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>Apply</button>
            )}
          </>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} style={{ ...inp, minWidth: 160 }}>
            <option value="">All Members</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      </div>

      {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem', padding: '0.7rem 1rem', color: '#DC2626', fontSize: '0.83rem' }}>{error}</div>}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', padding: '3rem' }}>
          <div style={{ width: 28, height: 28, border: '3px solid #E5E7EB', borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'wh-spin 0.8s linear infinite' }} />
          <span style={{ color: '#6B7280', fontSize: '0.83rem' }}>Loading…</span>
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.875rem' }}>
            {[
              { label: 'Total Hours',  value: `${fmt(data.total_hours)}h`, color: '#2563EB', bg: '#EFF6FF',  icon: '⏱' },
              { label: 'Entries',      value: data.total_entries,           color: '#059669', bg: '#F0FDF4',  icon: '📋' },
              { label: 'Members',      value: data.by_employee?.length || 0,color: '#D97706', bg: '#FFFBEB',  icon: '👥' },
              { label: 'Avg / Member', value: data.by_employee?.length ? `${fmt(data.total_hours / data.by_employee.length)}h` : '—', color: '#7C3AED', bg: '#F5F3FF', icon: '📊' },
            ].map((k, i) => (
              <div key={i} style={{ background: k.bg, borderRadius: '0.75rem', padding: '0.875rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem', opacity: 0.6 }}>{k.icon}</span>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 600, color: k.color, opacity: 0.75, marginTop: 2 }}>{k.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          {(data.by_employee?.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

              {/* Member bar chart */}
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0.75rem', padding: '1rem 1.1rem' }}>
                <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827', margin: '0 0 0.875rem' }}>Hours by Member</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.by_employee.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} width={80} />
                    <Tooltip formatter={v => [`${fmt(v)}h`, 'Hours']} contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                    <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
                      {data.by_employee.slice(0, 10).map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Daily hours chart */}
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0.75rem', padding: '1rem 1.1rem' }}>
                <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#111827', margin: '0 0 0.875rem' }}>Daily Hours</p>
                {data.by_date?.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.by_date} margin={{ top: 4, right: 8, bottom: 36, left: -10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6B7280' }} angle={-22} textAnchor="end" interval={0}
                        tickFormatter={d => { const dt = new Date(d); return `${dt.getDate()} ${dt.toLocaleString('en-IN', { month: 'short' })}`; }} />
                      <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                      <Tooltip formatter={v => [`${fmt(v)}h`, 'Hours']}
                        labelFormatter={d => new Date(d).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                        contentStyle={{ fontSize: 12, borderRadius: '0.5rem' }} />
                      <Bar dataKey="hours" fill={theme.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#9CA3AF', fontSize: '0.82rem' }}>No daily data</div>}
              </div>
            </div>
          )}

          {/* Entries table */}
          <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '0.75rem', overflow: 'hidden' }}>

            {/* Table header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1.1rem', borderBottom: '1px solid #F1F5F9', background: '#F8FAFC', flexWrap: 'wrap', gap: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#111827' }}>
                Timesheet Entries
                <span style={{ marginLeft: '0.5rem', background: '#E5E7EB', color: '#374151', borderRadius: '999px', padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>{entries.length}</span>
              </span>
              <input type="text" placeholder="Search member, description…" value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ padding: '0.35rem 0.75rem', border: '1.5px solid #E5E7EB', borderRadius: '0.5rem', fontSize: '0.8rem', outline: 'none', width: 220, background: '#fff' }} />
            </div>

            {entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏱</div>
                <p style={{ fontWeight: 600, color: '#374151', margin: '0 0 0.25rem' }}>No timesheet entries found</p>
                <p style={{ color: '#9CA3AF', fontSize: '0.82rem', margin: 0 }}>
                  {data.total_entries === 0
                    ? 'No hours have been logged for this project in the selected period.'
                    : 'No entries match your current search.'}
                </p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        {['Member', 'Date', 'Type', 'Description', 'Hours'].map(h => (
                          <th key={h} style={{ padding: '0.55rem 1rem', textAlign: 'left', fontWeight: 700, color: '#6B7280', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #F1F5F9', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e, i) => {
                        const ac = avatarColor(e.employee_name);
                        return (
                          <tr key={e.id} style={{ borderBottom: '1px solid #F8FAFC', transition: 'background 0.1s' }}
                            onMouseEnter={ev => ev.currentTarget.style.background = '#F8FAFC'}
                            onMouseLeave={ev => ev.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#FAFAFA'}>

                            {/* Member */}
                            <td style={{ padding: '0.65rem 1rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${ac}18`, color: ac, fontWeight: 800, fontSize: '0.72rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${ac}30`, flexShrink: 0 }}>
                                  {initials(e.employee_name)}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{e.employee_name}</div>
                                  <div style={{ fontSize: '0.68rem', color: '#9CA3AF', fontFamily: 'monospace' }}>{e.emp_code}</div>
                                </div>
                              </div>
                            </td>

                            {/* Date */}
                            <td style={{ padding: '0.65rem 1rem', color: '#6B7280', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                              {new Date(e.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })}
                            </td>

                            {/* Type */}
                            <td style={{ padding: '0.65rem 1rem' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: '0.7rem', fontWeight: 700, background: e.row_type === 'tender' ? '#FEF3C7' : '#EEF2FF', color: e.row_type === 'tender' ? '#92400E' : '#4338CA' }}>
                                {e.row_type === 'tender' ? '🏷 Tender' : '📂 Project'}
                              </span>
                            </td>

                            {/* Description */}
                            <td style={{ padding: '0.65rem 1rem', color: '#6B7280', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {e.description || <span style={{ color: '#D1D5DB', fontStyle: 'italic' }}>No description</span>}
                            </td>

                            {/* Hours */}
                            <td style={{ padding: '0.65rem 1rem' }}>
                              <span style={{ background: '#EFF6FF', color: '#1D4ED8', borderRadius: 8, padding: '3px 12px', fontWeight: 800, fontSize: '0.875rem' }}>
                                {fmt(e.hours)}h
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#F0F9FF', borderTop: '2px solid #BFDBFE' }}>
                        <td colSpan={4} style={{ padding: '0.65rem 1rem', fontWeight: 700, color: '#111827', fontSize: '0.82rem' }}>
                          Total ({entries.length} entries)
                        </td>
                        <td style={{ padding: '0.65rem 1rem' }}>
                          <span style={{ background: '#DBEAFE', color: '#1D4ED8', borderRadius: 8, padding: '3px 12px', fontWeight: 800, fontSize: '0.9rem' }}>
                            {fmt(filteredHours)}h
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {!loading && !data && !error && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#F8FAFC', borderRadius: '1rem', border: '2px dashed #E5E7EB' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏱</div>
          <p style={{ fontWeight: 600, color: '#374151', margin: 0 }}>Select a period to load work hours</p>
        </div>
      )}
    </div>
  );
}
